import { css } from 'aphrodite';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../../../web/components/Button';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { ApiClient } from '../../../services/api';
import { MrryDirection, MrryMessageType, MrryModal } from '../../../components/MrryModal';
import { VoiceAgentInterface as VoiceAgentInterfaceComponent } from '../../components/VoiceAgentInterface';
import { VoiceSelector } from '../../components/VoiceSelector';
import { usePracticeCallMutations } from '../../hooks/usePracticeCallMutations';
import { useRepCoachingGoals } from '../../hooks/useRepCoachingGoals';
import { VoiceAgent } from '../../services/VoiceAgent';
import { buildSystemPrompt } from '../../services/VoiceAgentPromptService';
import { PitchType, Provider, TranscriptEntry, VoiceAgentModalProps, VoiceAgentStatus, VoiceGender } from '../../types';
import { styleSheet } from './styles';

export const VoiceAgentModal: React.FC<VoiceAgentModalProps> = ({ isOpen, onClose, leadId, contact, company }) => {
	const [pitchType, setPitchType] = useState<PitchType>('cold-call');
	const [provider, _] = useState<Provider>('elevenlabs');
	const [voiceGender, setVoiceGender] = useState<VoiceGender>('female');
	const [status, setStatus] = useState<VoiceAgentStatus>({
		isConnected: false,
		isConnecting: false,
	});
	const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
	const [voiceAgent, setVoiceAgent] = useState<import('../../types').VoiceAgentInterface | null>(null);
	const [phoneCallId, setPhoneCallId] = useState<string | null>(null);
	const { openAI, elevenLabs } = usePracticeCallMutations();
	const userSession = useUserSession();
	const { data: repCoachingGoals } = useRepCoachingGoals(userSession.user.id);

	// Reset state when modal opens and infer gender from contact
	useEffect(() => {
		if (isOpen) {
			setTranscript([]);
			setStatus({ isConnected: false, isConnecting: false });
			setPhoneCallId(null);
			if (voiceAgent) {
				voiceAgent.disconnect();
				setVoiceAgent(null);
			}

			// Auto-infer voice gender from contact's first name
			if (contact?.firstName) {
				// TODO: infer gender from contact
				setVoiceGender('female');
			} else {
				// Reset to default if no contact info
				setVoiceGender('female');
			}
		}
	}, [isOpen, contact?.firstName]); // eslint-disable-line react-hooks/exhaustive-deps

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (voiceAgent) {
				voiceAgent.disconnect();
			}
		};
	}, [voiceAgent]);

	const handleConnect = useCallback(async () => {
		try {
			setStatus({ isConnected: false, isConnecting: true });

			// Clean up any existing connection
			if (voiceAgent) {
				voiceAgent.disconnect();
				setVoiceAgent(null);
			}

			// Call the appropriate API based on selected provider
			const mutation = provider === 'openai' ? openAI : elevenLabs;
			const response = await mutation.mutateAsync({
				companyId: company.id,
				leadServedId: leadId,
				contactId: contact.id,
			});

			// Extract data from response - handle different property names
			const {
				clientToken,
				leadAIContext,
				agentTemplate,
				externalConversationToken: externalConversationId,
				phoneCallId: responsePhoneCallId,
			} = response;
			const objectivePrompt = agentTemplate.objectivePrompt;

			// Store phoneCallId for later use
			if (responsePhoneCallId) {
				setPhoneCallId(responsePhoneCallId);
			}

			const prompt = buildSystemPrompt(
				contact,
				objectivePrompt,
				pitchType,
				leadAIContext,
				repCoachingGoals || { workingOn: '', tysonMaxDifficulty: 1 }
			);

			const apiToken = externalConversationId ?? clientToken;
			// Validate that we have the required token for the selected provider
			if (!clientToken) {
				throw new Error(
					`Missing client token for ${provider} provider. Backend API response: ${JSON.stringify(response)}`
				);
			}

			// Create new voice agent with provider-specific token
			const newAgent = new VoiceAgent({
				apiKey: apiToken,
				onTranscriptUpdate: setTranscript,
				voiceGender,
				provider,
			});

			// Connect with custom prompt, contact info, and voice gender
			await newAgent.connect(pitchType, prompt, voiceGender);
			setVoiceAgent(newAgent);
			setStatus({ isConnected: true, isConnecting: false });

			// Update phoneCall with conversationId if both are available
			if (responsePhoneCallId) {
				const conversationId = newAgent.getConversationId();
				if (conversationId) {
					const apiClient = new ApiClient(userSession);
					try {
						await apiClient.updatePhoneCallConversationId(responsePhoneCallId, conversationId);
					} catch (error) {
						console.error('Failed to update phoneCall with conversationId:', error);
					}
				}
			}
		} catch (error) {
			// Connection failed
			setStatus({
				isConnected: false,
				isConnecting: false,
				error: {
					code: 'CONNECTION_FAILED',
					message: error instanceof Error ? error.message : 'Failed to connect to voice agent',
				},
			});
		}
	}, [
		pitchType,
		provider,
		voiceGender,
		voiceAgent,
		contact,
		company,
		leadId,
		openAI,
		elevenLabs,
		repCoachingGoals,
		userSession,
	]);

	const handleDisconnect = useCallback(async () => {
		if (voiceAgent) {
			voiceAgent.disconnect();
			setVoiceAgent(null);
		}
		setStatus({ isConnected: false, isConnecting: false });

		// Notify platform that practice call ended
		if (phoneCallId) {
			const apiClient = new ApiClient(userSession);
			try {
				await apiClient.notifyPracticeCallEnded(phoneCallId);
			} catch (error) {
				console.error('Failed to notify platform of practice call end:', error);
			}
		}
	}, [voiceAgent, phoneCallId, userSession]);

	const handleClose = useCallback(async () => {
		if (voiceAgent) {
			voiceAgent.disconnect();
			setVoiceAgent(null);
		}
		setStatus({ isConnected: false, isConnecting: false });

		// Notify platform that practice call ended
		if (phoneCallId) {
			const apiClient = new ApiClient(userSession);
			try {
				await apiClient.notifyPracticeCallEnded(phoneCallId);
			} catch (error) {
				console.error('Failed to notify platform of practice call end:', error);
			}
		}

		onClose();
	}, [voiceAgent, phoneCallId, userSession, onClose]);

	const handleClearTranscript = useCallback(() => {
		if (voiceAgent) {
			voiceAgent.clearTranscript();
		}
		setTranscript([]);
	}, [voiceAgent]);

	const handleDownloadTranscript = useCallback(() => {
		if (!voiceAgent) return;

		const transcriptText = voiceAgent.getFormattedTranscript();
		if (!transcriptText) {
			alert('No transcript available to download');
			return;
		}

		const blob = new Blob([transcriptText], { type: 'text/plain' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `voice-agent-transcript-${new Date().toISOString().split('T')[0]}.txt`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}, [voiceAgent]);

	if (!isOpen) return null;

	const modalContent = (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.header)}>
				<h2 className={css(styleSheet.title)}>Voice Agent Practice Session</h2>
				<p className={css(styleSheet.subtitle)}>Practice your sales call with an AI-powered prospect</p>
			</div>

			{!status.isConnected && !status.isConnecting && (
				<div className={css(styleSheet.setupSection)}>
					{/* Hidden pitch type selector - functionality preserved but visually hidden */}
					<div className={css(styleSheet.pitchTypeSection)} style={{ display: 'none' }}>
						<label className={css(styleSheet.label)}>Pitch Type:</label>
						<select
							value={pitchType}
							onChange={e => setPitchType(e.target.value as PitchType)}
							className={css(styleSheet.select)}
						>
							<option value='cold-call'>Cold Call</option>
							<option value='product-demo'>Product Demo</option>
							<option value='follow-up'>Follow-up Call</option>
							<option value='objection-handling'>Objection Handling</option>
						</select>
					</div>

					<VoiceSelector selectedGender={voiceGender} onGenderChange={setVoiceGender} disabled={status.isConnecting} />

					{status.error && <div className={css(styleSheet.error)}>{status.error.message}</div>}

					<div className={css(styleSheet.buttonSection)}>
						<Button
							kind='primary'
							onClick={handleConnect}
							disabled={status.isConnecting}
							className={css(styleSheet.connectButton)}
						>
							{status.isConnecting ? 'Connecting...' : 'Start Voice Session'}
						</Button>
					</div>
				</div>
			)}

			{(status.isConnected || status.isConnecting) && (
				<VoiceAgentInterfaceComponent
					isConnected={status.isConnected}
					isConnecting={status.isConnecting}
					transcript={transcript}
					onDisconnect={handleDisconnect}
					onClearTranscript={handleClearTranscript}
					onDownloadTranscript={handleDownloadTranscript}
					error={status.error}
				/>
			)}
		</div>
	);

	return (
		<MrryModal
			fromDirection={MrryDirection.FromTop}
			message={{
				content: modalContent,
				duration: 0, // Keep open until manually closed
				type: MrryMessageType.Normal,
			}}
			onRequestClose={handleClose}
			internalCloseButton={true}
		/>
	);
};
